import {useCallback, useReducer} from 'react';

import {MAX_REACTION_CHEM_SEARCH_CLAUSES} from 'chemistry/ChemistryConstants';
import {ChemicalStructureDrawerValue} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {
  createReactionSearchClauseDraft,
  getInitialReactionSearchClauses,
  ReactionSearchClauseDraft,
} from 'chemistry/ReactionChemSearchUtils';
import {SearchParams} from 'search/SearchConstants';

interface ReactionChemSearchModalState {
  clauses: Array<ReactionSearchClauseDraft>;
  activeClauseId: string;
}

type ReactionChemSearchModalAction =
  | {type: 'SELECT_TAB'; clauseId: string}
  | {
      type: 'UPDATE_CLAUSE';
      clauseId: string;
      patch: Partial<Omit<ReactionSearchClauseDraft, 'id'>>;
    }
  | {type: 'ADD_CLAUSE'; clause: ReactionSearchClauseDraft}
  | {type: 'REMOVE_CLAUSE'; clauseId: string};

function getFallbackActiveClauseId(clauses: Array<ReactionSearchClauseDraft>): string {
  return clauses[0]?.id ?? '';
}

function reactionChemSearchModalReducer(
  state: ReactionChemSearchModalState,
  action: ReactionChemSearchModalAction
): ReactionChemSearchModalState {
  switch (action.type) {
    case 'SELECT_TAB':
      return {...state, activeClauseId: action.clauseId};
    case 'UPDATE_CLAUSE':
      return {
        ...state,
        clauses: state.clauses.map((clause) =>
          clause.id === action.clauseId ? {...clause, ...action.patch} : clause
        ),
      };
    case 'ADD_CLAUSE': {
      if (state.clauses.length >= MAX_REACTION_CHEM_SEARCH_CLAUSES) {
        return state;
      }
      return {
        clauses: [...state.clauses, action.clause],
        activeClauseId: action.clause.id,
      };
    }
    case 'REMOVE_CLAUSE': {
      if (state.clauses.length <= 1) {
        return state;
      }
      const removedClauseIndex = state.clauses.findIndex((clause) => clause.id === action.clauseId);
      if (removedClauseIndex < 0) {
        return state;
      }
      const nextClauses = state.clauses.filter((clause) => clause.id !== action.clauseId);
      let nextActiveClauseId = state.activeClauseId;
      if (state.activeClauseId === action.clauseId) {
        const successorIndex = Math.min(removedClauseIndex, nextClauses.length - 1);
        nextActiveClauseId = nextClauses[successorIndex]?.id ?? getFallbackActiveClauseId(nextClauses);
      }
      return {clauses: nextClauses, activeClauseId: nextActiveClauseId};
    }
    default:
      return state;
  }
}

interface UseReactionChemSearchModalStateArgs {
  searchParams: SearchParams;
  initialMol: ChemicalStructureDrawerValue;
  defaultMinSimilarity: number;
}

interface UseReactionChemSearchModalStateResult {
  clauses: Array<ReactionSearchClauseDraft>;
  activeClauseId: string;
  activeClause: ReactionSearchClauseDraft;
  selectTab: (clauseId: string) => void;
  updateClause: (clauseId: string, patch: Partial<Omit<ReactionSearchClauseDraft, 'id'>>) => void;
  addClause: () => void;
  removeClause: (clauseId: string) => void;
}

export function useReactionChemSearchModalState({
  searchParams,
  initialMol,
  defaultMinSimilarity,
}: UseReactionChemSearchModalStateArgs): UseReactionChemSearchModalStateResult {
  const [state, dispatch] = useReducer(reactionChemSearchModalReducer, undefined, () => {
    const clauses = getInitialReactionSearchClauses(searchParams, initialMol, defaultMinSimilarity);
    return {
      clauses,
      activeClauseId: clauses[0]?.id ?? '',
    };
  });
  const activeClause = state.clauses.find((clause) => clause.id === state.activeClauseId) ?? state.clauses[0];

  const selectTab = useCallback((clauseId: string): void => {
    dispatch({type: 'SELECT_TAB', clauseId});
  }, []);

  const updateClause = useCallback(
    (clauseId: string, patch: Partial<Omit<ReactionSearchClauseDraft, 'id'>>): void => {
      dispatch({type: 'UPDATE_CLAUSE', clauseId, patch});
    },
    []
  );

  const addClause = useCallback((): void => {
    dispatch({
      type: 'ADD_CLAUSE',
      clause: createReactionSearchClauseDraft({
        chemicalStructureQuery: null,
        minSimilarity: defaultMinSimilarity,
        reactionComponentRole: null,
      }),
    });
  }, [defaultMinSimilarity]);

  const removeClause = useCallback((clauseId: string): void => {
    dispatch({type: 'REMOVE_CLAUSE', clauseId});
  }, []);

  return {
    clauses: state.clauses,
    activeClauseId: state.activeClauseId,
    activeClause,
    selectTab,
    updateClause,
    addClause,
    removeClause,
  };
}
